/**
 * The DOM-writing half of the reference recompute(): renders a TripComputation
 * into the time cells, dropdown annotations, day footers, warnings, dup flags —
 * and grows/shrinks each day's extra slots to match the time actually left.
 */
import {
  EXTRA_SLOT_MIN,
  MAX_EXTRA_SLOTS,
  computeTrip,
  type DayComputation,
  type FixedRowComp,
  type SlotRowComp,
  type TripComputation,
} from "@trip/shared/engine/schedule";
import { esc, fmin, t12 } from "@trip/shared/engine/time";
import { isFixed } from "@trip/shared/types";
import { store } from "../store.js";
import { isFree, reservationOf } from "./price.js";
import { extraRowHTML } from "./render.js";
import { refreshSlot } from "./slots.js";

/** "3h 40m" */
const hm = (min: number) => {
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return h ? `${h}h${m ? ` ${m}m` : ""}` : `${m}m`;
};

/** "America/Chicago" → "Chicago time" */
const tzCity = (tz: string) => `${(tz.split("/").pop() ?? tz).replace(/_/g, " ")} time`;

/** Left-rail cost + reservation signals, now two INDEPENDENT axes:
 *  price — FREE badge when the activity is free, the price text when it
 *  costs something (never both; the duplicate "FREE + Free in red" is the
 *  bug this replaces);
 *  reservation — every activity carries either ⚑ BOOK AHEAD or WALK-UP,
 *  styled as siblings. */
function railFare(dayId: string, r: SlotRowComp): string {
  const st = store.slots[`${dayId}_${r.i}`];
  const a = st && store.def.act[st.act];
  if (!a || a.nomodal) return "";
  let out = "";
  if (isFree(a.price, a.badge)) {
    out += `<span class="badge b-free">FREE</span>`;
  } else if (a.price) {
    let p = a.price.split(/[—·]/)[0].trim();
    if (p.length > 26) p = p.slice(0, 24) + "…";
    out += `<span class="fare">${esc(p)}</span>`;
  }
  const res = reservationOf(a.badge);
  if (res === "book") out += `<span class="badge b-book">⚑ BOOK AHEAD</span>`;
  else if (res === "walkup") out += `<span class="badge b-walkup" title="No reservation needed">WALK-UP</span>`;
  else out += `<span class="badge b-unknown" title="Nobody has recorded whether this needs booking — set it in the activity editor">RESERVATION?</span>`;
  return out;
}

/**
 * The travel leg DEPARTING a row, phrased as "to the next stop". Each slot
 * row's own `travel` is the leg INTO it (its start already includes it), so
 * the chip for row i comes from the next travelling row — or from backAtBase
 * on the day's last stop. Rendering departure on the row you leave is what
 * makes "ends 4:30, 25 min to the next place, next starts 4:55" read as one
 * chain instead of a puzzle.
 */
function departingTravelHTML(c: DayComputation, after: number): string {
  for (let j = after + 1; j < c.rows.length; j++) {
    const row = c.rows[j];
    if (!row || row.kind !== "slot") break; // a fixed pin breaks the modeled chain
    if (row.skipped) continue; // travel chains across skipped rows
    if (row.travel.min > 0) return `<small class="trav">${row.travel.icon || "🚶"} ${row.travel.min} min to next</small>`;
    return "";
  }
  const last = c.rows.slice(after + 1).every((row) => !row || (row.kind === "slot" && row.skipped));
  if (last && c.backAtBase && c.backAtBase.travel.min > 0) {
    return `<small class="trav">${c.backAtBase.travel.icon || "🚶"} ${c.backAtBase.travel.min} min back to base</small>`;
  }
  return "";
}

function renderSlotCell(dayId: string, cell: HTMLElement, r: SlotRowComp, c: DayComputation, i: number) {
  if (r.skipped) {
    cell.innerHTML = "—";
    cell.classList.remove("late");
    return;
  }
  cell.innerHTML = fmin(r.startMin) + `<small>→ ${fmin(r.endMin)}</small>` + departingTravelHTML(c, i) + railFare(dayId, r);
  cell.classList.toggle("late", r.late);
}

/** The clock-shift chip under a slot whose zone differs from the stop before. */
function renderTzWarn(dayId: string, r: SlotRowComp) {
  const el = document.getElementById(`tzw_${dayId}_${r.i}`);
  if (!el) return;
  if (!r.tzChange || r.skipped) {
    el.style.display = "none";
    return;
  }
  const { from, to, deltaMin } = r.tzChange;
  const sign = deltaMin > 0 ? "+" : "−";
  el.style.display = "";
  el.textContent = `⏰ Time zone changes here: ${tzCity(from)} → ${tzCity(to)} (${sign}${hm(Math.abs(deltaMin))}). Double-check bookings against local clocks.`;
}

/**
 * Slots appear and disappear with the time actually available: while a day
 * still has EXTRA_SLOT_MIN of unspoken-for time before its next fixed anchor
 * (or its back-at-base target), one more empty row is on offer; picks fill it
 * and may earn another. State lives under keys like "d13_x1", so definition
 * slots never renumber.
 */
function syncExtraRows(trip: TripComputation): boolean {
  const def = store.def;
  let changed = false;
  for (const d of def.days) {
    const comp: DayComputation = trip.days[d.id];
    const cont = document.getElementById(`xrows_${d.id}`);
    if (!cont || !comp.extra) continue;
    const ex = comp.extra;
    const filled = ex.keys.length;
    const offerBlank = store.role === "edit" && filled < MAX_EXTRA_SLOTS && ex.slackMin >= EXTRA_SLOT_MIN;
    const desired = filled + (offerBlank ? 1 : 0);
    const have = cont.querySelectorAll(".xrow").length;
    if (have !== desired) {
      const hint = offerBlank
        ? `about ${hm(ex.slackMin)} free${ex.anchorLabel ? ` until ${ex.anchorLabel}` : ""}`
        : "";
      cont.innerHTML = Array.from({ length: desired }, (_, idx) =>
        extraRowHTML(d.id, idx + 1, ex.opts, idx === desired - 1 && offerBlank ? hint : "")
      ).join("");
      for (let n = 1; n <= desired; n++) refreshSlot(d.id, `x${n}`);
      changed = true;
    } else if (offerBlank && desired > 0) {
      // keep the hint's remaining-time figure honest as picks change
      const label = cont.querySelectorAll(".xhint")[desired - 1];
      if (label) label.textContent = ` — about ${hm(ex.slackMin)} free${ex.anchorLabel ? ` until ${ex.anchorLabel}` : ""}`;
    }
  }
  return changed;
}

/**
 * The last computed schedule, so surfaces outside the render pass (the
 * activity picker, which needs each option's travel cost and where an
 * activity is already scheduled) can read it without recomputing the whole
 * trip on a click.
 *
 * It lives here rather than on `store` because the store is plain synced
 * state; a derived cache sitting next to it invites being read as truth
 * after a mutation but before the recompute. Every change path ends in
 * recomputeAndRender, which refreshes this.
 */
let _lastTrip: TripComputation | null = null;

export function currentTrip(): TripComputation {
  return (_lastTrip ??= computeTrip(store.def, store.slots, store.dayEnds, store.fixDurs));
}

/** Drop the memo — called before a recompute so nothing reads a stale trip. */
export function invalidateTrip(): void {
  _lastTrip = null;
}

export function recomputeAndRender() {
  const def = store.def;
  const trip = computeTrip(def, store.slots, store.dayEnds, store.fixDurs);
  _lastTrip = trip;

  // adding/removing an extra row changes what exists in the DOM before this
  // pass fills it in, so the rows must be settled first
  syncExtraRows(trip);

  for (const d of def.days) {
    const c = trip.days[d.id];
    d.items.forEach((it, i) => {
      const cell = document.getElementById(`t_${d.id}_${i}`);
      if (!cell) return;
      const row = c.rows[i]!;
      if (isFixed(it)) {
        const fr = row as FixedRowComp;
        // Flights get a boarding estimate: gates close ~20 min out, boarding
        // opens ~40 — the tilde is the honesty marker (airlines vary), and
        // the airline's own time on the boarding pass wins when you have it.
        const isFlight = (it.ic + it.l).includes("✈");
        const boarding = isFlight ? `<small class="board">🛄 board ${fmin(fr.tMin - 40)}</small>` : "";
        // an override replaces the item's own end (if any) with the effective
        // one; an open-ended item with neither keeps just its start — the end
        // genuinely isn't known, and the fixdur select is how you set it
        cell.innerHTML =
          boarding +
          t12(it.t) +
          (fr.ovMin ? `<small>→ ${fmin(fr.eMin!)}</small>` : it.e ? `<small>→ ${t12(it.e)}</small>` : "") +
          departingTravelHTML(c, i);
        cell.classList.remove("late");
        return;
      }
      const r = row as SlotRowComp;
      renderSlotCell(d.id, cell, r, c, i);
      renderTzWarn(d.id, r);
    });

    /* live booked/needs-booking flags on fixed transport rows */
    d.items.forEach((it, i) => {
      if (!isFixed(it) || !it.blk) return;
      const flag = document.getElementById(`blf_${d.id}_${i}`);
      if (!flag) return;
      const booked = Boolean(store.checks[it.blk]);
      flag.style.display = "";
      flag.className = "blflag " + (booked ? "blflag-ok" : "blflag-need");
      flag.textContent = booked ? "✔ Booked" : "⚑ Booking required";
      flag.title = booked
        ? "Marked booked on the checklist — click to undo"
        : "Not booked yet — click once it's done (also ticks the checklist)";
    });

    /* extra rows ride at the tail of the rows array, keyed x1, x2 … */
    for (const [tail, row] of c.rows.slice(d.items.length).entries()) {
      const r = row as SlotRowComp;
      const cell = document.getElementById(`t_${d.id}_${r.i}`);
      if (!cell) continue;
      renderSlotCell(d.id, cell, r, c, d.items.length + tail);
      renderTzWarn(d.id, r);
    }

    const endEl = document.getElementById(`end_${d.id}`);
    if (endEl) {
      if (c.backAtBase) {
        const tv = c.backAtBase.travel;
        endEl.innerHTML =
          `🏨 Back at your base <b>${fmin(c.backAtBase.min)}</b>` +
          (tv.min >= 5 ? ` <span class="trav">(${tv.icon || "🚶"} ${tv.min} min from the last stop)</span>` : "");
        endEl.classList.toggle("late", c.backAtBase.late);
        endEl.style.display = "";
      } else {
        endEl.style.display = "none";
      }
    }

    const w = document.getElementById(`warn_${d.id}`);
    if (w) {
      if (c.warningKind === "deadline") {
        w.textContent = "⚠ " + t12(c.warning!);
        w.style.display = "block";
      } else if (c.warningKind === "late") {
        w.textContent = "⚠ " + c.warning!;
        w.style.display = "block";
      } else {
        w.style.display = "none";
      }
    }
  }

  /* duplicate highlighting */
  for (const [key] of Object.entries(store.slots)) {
    const isDup = trip.duplicates.has(key);
    const row = document.getElementById("row_" + key);
    const [dayId, idx] = [key.slice(0, key.indexOf("_")), key.slice(key.indexOf("_") + 1)];
    const flag = document.getElementById(`dup_${dayId}_${idx}`);
    if (row) row.classList.toggle("dup", isDup);
    if (flag) flag.style.display = isDup ? "" : "none";
  }

  return trip;
}
